/* ---------------------------------------------------------------------------
   CFCities — what a city picker's slot can hold
   ---------------------------------------------------------------------------

   The data layer under <bcv2-multi-select> (assets/cf-city-picker.js): the
   visitor's own custom locations, and the two rules that decide how a city
   reads — whether it is orderable, and what its row says.

   It came out of bundle-configurator-v2.liquid together with the picker. The
   picker moved first and the data stayed behind, which meant the trio product
   could render the control but not fill it. These are the pieces that have to
   agree between the two pages: a city disabled on one page and orderable on
   the other, or two different labels for the same clearance CUBE, would be a
   bug nothing on either page could see.

       CFCities.readDesigns()            this browser's designs, newest first
       CFCities.designLink(design)       back into the configurator, on it
       CFCities.isOutOfStock(variant)
       CFCities.label(variant, strings)
       CFCities.isCustomId(id)
       CFCities.lineItem(variant, extra)
       CFCities.resolveHeights(slotIds)  await before lineItem: picks the box
       CFCities.CUSTOM_PREFIX

   `label` takes its strings rather than reading a global, because the two
   sections translate from different namespaces. It wants four keys —
   warehouse_sale, clearance_sale, savings_suffix, out_of_stock_suffix — with
   __N__ for the city name and __P__ for the percentage.

   Both globals it reads, CF_CUBE_CUSTOM and CF_CUBE_VARIANTS, are written by
   snippets/cf-cube-catalogue.liquid.

   READ THE DESIGNS AT PARSE TIME, still: the note in cfReadCustomDesigns is
   about the caller, not this file. The picker renders when configure() is
   called, so a host that reads its designs late shows a picker without them.
--------------------------------------------------------------------------- */

(function () {
  if (window.CFCities) return;

  const CUSTOM_PREFIX = 'custom:';

  /*
   * The step-3 product shot for a design - the REGULAR model composite, which
   * is what a catalogue city's picture is too. `preview_url` is the angled hero
   * from the map step: a different shot of a different thing, and full size,
   * which is why the slots were slow to fill. addon-400.jpg is the composite,
   * already 400px for a slot that is 200px at its widest.
   *
   * BUILT FROM THE API BASE AND THE DESIGN ID, which is how both of the other
   * places that want this image do it - upgradeThumbToAddon and verifyOrDrop in
   * cc-configurator-designs.js. Rewriting the tail of preview_url looked
   * cheaper and is not the same thing: that URL is whatever the API chose to
   * return, and neither of those two trusts its shape either.
   *
   * addon-400, never addon: the API's /img route falls back to serving
   * preview.jpg for an unsized `addon.jpg` while the composite is still
   * rendering, which is the wrong picture served with a 200. The sized one
   * 404s honestly instead.
   */
  // `f=45`: the composite turned 4:5 on 2026-10-01 under the same name, and the route lets a
  // browser reuse what it holds for a day while it re-checks; a new URL is never served stale.
  function cfCompositeUrl(designId, name) {
    const base = ((window.CF_CUBE_CUSTOM || {}).previewApiUrl || '').replace(/\/$/, '');
    if (!base || !designId) return '';
    return `${base}/img/${encodeURIComponent(designId)}/${name}?f=45`;
  }

  /*
   * What a slot tries, and why it is exactly these two.
   *
   * Read off the API's own /img route (pipeline/save_api.py):
   *
   *   addon.jpg      the composite, 2048x2560 (4:5; 1200x1500 until
   *                  2026-10-31). WHEN IT IS MISSING THE ROUTE
   *                  SERVES preview.jpg - the angled hero, with a 200 - so a
   *                  design still rendering silently shows the wrong picture
   *                  and nothing can detect it.
   *   addon-400.jpg  the one downscale the pipeline writes. Missing, it falls
   *                  back to the full addon.jpg, and 404s honestly when there
   *                  is no composite at all.
   *   addon-800.jpg  does not exist. The pipeline dropped the 800 as unused and
   *                  the route's filename pattern does not even admit it - it
   *                  is a guaranteed 404, which is what my last attempt spent a
   *                  request per slot discovering.
   *
   * So: ask for the 400, which is the honest one, and once it has loaded -
   * proof that the composite exists - upgrade to the full image, which can no
   * longer fall back to the hero. The reader gets something immediately and a
   * sharp one a moment later, which is what a 200px slot needs on a 3x screen.
   */
  function cfImageCandidates(design) {
    return [
      cfCompositeUrl(design.design_id, 'addon-400.jpg'),
      design.preview_url || ''
    ].filter(Boolean);
  }

  /*
   * Degrees, minutes and seconds - because that is what a catalogue city's
   * plaque says. The `custom.coordinates` metafield holds e.g.
   * "50°46′29.09″N, 6°5′1.89″E", so a decimal pair from a design would have
   * put two different notations on two plaques of the same bundle.
   *
   * Prime and double-prime, not apostrophes, and no leading zeros on the
   * minutes - matched to the stored strings character for character.
   */
  function cfDms(value, pos, neg) {
    if (!Number.isFinite(value)) return '';
    const hemi = value >= 0 ? pos : neg;
    const abs = Math.abs(value);
    let deg = Math.floor(abs);
    let min = Math.floor((abs - deg) * 60);
    let sec = ((abs - deg) * 60 - min) * 60;
    // 59.999 seconds must not print as 60.00.
    if (Number(sec.toFixed(2)) >= 60) { sec = 0; min += 1; }
    if (min >= 60) { min = 0; deg += 1; }
    return `${deg}°${min}′${sec.toFixed(2)}″${hemi}`;
  }

  function cfReadCustomDesigns() {
    const cfg = window.CF_CUBE_CUSTOM || {};
    if (!cfg.variantId) return [];

    let raw = [];
    try { raw = JSON.parse(localStorage.getItem(cfg.bufferKey)) || []; }
    catch (_) { return []; }          // private mode, or a corrupt payload
    if (!Array.isArray(raw)) return [];

    return raw
      .filter(d => d && d.design_id)
      .map(d => {
        // `city` is stored from the geocoder on designs made from now on. Older
        // entries have only "City, Country" in place_name, so the first part of
        // it stands in - and the country-based plaque lines simply come out
        // empty for those, rather than wrong.
        const city = (d.city || (d.place_name || '').split(',')[0] || '').trim();
        const lat = Number(d.lat);
        const lon = Number(d.lon);

        return {
          id: CUSTOM_PREFIX + d.design_id,
          custom: true,
          design: d,
          priceCents: Number(cfg.priceCents) || 0,
          baseCity: city || d.design_id,
          localname: '',
          englishName: city,
          variantCity: d.area || '',
          countryname: d.country || '',
          countrycode: (d.country_code || '').toUpperCase(),
          coordinates: (Number.isFinite(lat) && Number.isFinite(lon))
            ? `${cfDms(lat, 'N', 'S')}, ${cfDms(lon, 'E', 'W')}` : '',
          scale: d.scale ? `1:${d.scale}` : '',
          // The composite, under the name the wall grid already reads. Same
          // <img>, same slot, same loading treatment as a catalogue city - the
          // design simply supplies the picture instead of the CDN. The hero is
          // kept as the fallback for a design whose composite has not finished
          // rendering, so a slot shows the right thing or something rather
          // than pulsing forever.
          imgCandidates: cfImageCandidates(d),
          imgPreview: cfImageCandidates(d)[0] || '',
          // Only after the sized one loads - see the note above.
          imgUpgrade: cfCompositeUrl(d.design_id, 'addon.jpg'),
          imgFull: cfCompositeUrl(d.design_id, 'addon.jpg') || d.preview_url || '',
          // Made to order, which is exactly what `backorder` means to
          // isOutOfStock() - so an empty shelf never disables one.
          inventoryQty: 0,
          backorder: true,
          isFavorite: false,
          savingsPercent: 0,
          clearance: false
        };
      })
      .reverse();                     // the buffer appends; newest reads first
  }

  /*
   * A link back into the configurator, opened ON the design rather than on the
   * empty map: `design=` attaches to that exact one, and `saved=1` is what
   * makes it advance to step 2 instead of leaving the shopper at step 1 with
   * the model already loaded behind them. The coordinates ride along as the
   * regenerate fallback for a design the server has since evicted - the same
   * query the configurator's own share link builds.
   */
  function cfDesignLink(design) {
    const base = (window.CF_CUBE_CUSTOM || {}).configuratorUrl;
    if (!base || !design || !design.design_id) return '';
    const u = new URL(base, window.location.origin);
    u.searchParams.set('design', design.design_id);
    if (Number.isFinite(Number(design.lat)))      u.searchParams.set('lat', design.lat);
    if (Number.isFinite(Number(design.lon)))      u.searchParams.set('lon', design.lon);
    if (Number.isFinite(Number(design.scale)))    u.searchParams.set('scale', design.scale);
    if (Number.isFinite(Number(design.rotation))) u.searchParams.set('rotation', design.rotation);
    u.searchParams.set('saved', '1');
    return u.toString();
  }

  // A city flagged `backorder` is a new one we produce to order, so an empty
  // shelf does not stop the sale. Liquid sets the flag from
  // cube-backorder-allowed. Single stock test for the three places that gate on
  // it — the label suffix, the hidden <select>, and the multi-select rows — so
  // a city can never read as orderable in one and disabled in another.
  function isOutOfStock(variant) {
    if (!variant) return false;
    if (variant.backorder === true) return false;
    return Number(variant.inventoryQty || 0) <= 0;
  }

  function buildCityLabel(variant, t) {
    let cityName = variant.baseCity || '';
    if (variant.localname && variant.localname !== variant.baseCity) cityName += ' / ' + variant.localname;
    if (variant.variantCity) cityName += ' - ' + variant.variantCity;

    let finalLabel = cityName;
    const discountSuffix = variant.savingsPercent > 0
      ? t.savings_suffix.replace('__P__', variant.savingsPercent)
      : '';

    if (variant.saleReason === 'Warehouse Sale') {
      finalLabel = t.warehouse_sale.replace('__N__', cityName) + discountSuffix;
    } else if (variant.clearance) {
      finalLabel = t.clearance_sale.replace('__N__', cityName) + discountSuffix;
    }
    if (variant.isFavorite === true) finalLabel += ' ★';
    if (isOutOfStock(variant)) finalLabel += t.out_of_stock_suffix;

    return finalLabel;
  }

  /*
   * THE CART LINE FOR WHATEVER A SLOT HOLDS.
   *
   * A catalogue city is its own variant and needs nothing. A custom location is
   * a DIFFERENT product — cube-custom-location — and the design has to travel
   * with it as line-item properties, or the order says "CUBE Custom Location"
   * and nothing about which location.
   *
   * That product has two variants, identical but for box_size and weight. The
   * design's printed height picks one: under largeBoxFromMm is the small box,
   * anything else — including a height nobody could fetch — the large one, since
   * a tall model in a small box is the failure that matters. Callers await
   * resolveHeights() first; saved designs do not carry their height.
   *
   * The property names are the custom configurator's own, deliberately: an order
   * placed from the map, from a bundle or from a trio should read the same, and
   * fulfilment should not need a case per surface. Coordinates are decimal here
   * and DMS on the plaque, for the same reason — each matches what its own
   * neighbour already writes.
   *
   * `extra` is the caller's mark (`_bundle` or `_trio`), merged in rather than
   * assumed, because the two sets are keyed on different properties.
   *
   * Returns null for a variant it cannot place, so a caller can refuse to post
   * rather than post a NaN id — which /cart/add.js accepts and turns into a line
   * nobody ordered.
   */
  function cityLineItem(variant, extra) {
    if (!variant || variant.id === undefined || variant.id === null) return null;
    const properties = Object.assign({}, extra || {});

    if (!String(variant.id).startsWith(CUSTOM_PREFIX)) {
      const id = Number(variant.id);
      return Number.isFinite(id) && id > 0 ? { id, quantity: 1, properties } : null;
    }

    const cfg = window.CF_CUBE_CUSTOM || {};
    const d = variant.design;
    const place = d ? cfPlaceLabel(d) : '';
    if (place) properties['Location'] = place;
    const h = d ? cfHeights[d.design_id] : undefined;
    const fitsSmall = typeof h === 'number' && h < cfg.largeBoxFromMm;
    const id = Number(fitsSmall ? cfg.variantId : (cfg.variantIdLarge || cfg.variantId));
    if (!Number.isFinite(id) || id <= 0) return null;

    if (d) {
      if (d.scale) properties['Scale'] = `1:${d.scale}`;
      if (d.rotation !== undefined && d.rotation !== null) properties['Rotation'] = `${d.rotation}\u00b0`;
      if (Number.isFinite(Number(d.lat)) && Number.isFinite(Number(d.lon))) {
        properties['Coordinates'] = `${Number(d.lat).toFixed(4)}, ${Number(d.lon).toFixed(4)}`;
      }
      properties['Design ID'] = d.design_id;
      if (d.trees) properties['Trees'] = 'Yes';
      if (d.streets) properties['Streets'] = 'Yes';
    }
    return { id, quantity: 1, properties };
  }

  // The place as a name beside the coordinates. The district is dropped when the
  // geocoder returns it as the city too; older entries have only place_name.
  function cfPlaceLabel(design) {
    const d = design || {};
    const city = (d.city || '').trim();
    const area = (d.area || '').trim();
    const country = (d.country || '').trim();
    const parts = [];
    if (area && area.toLowerCase() !== city.toLowerCase()) parts.push(area);
    if (city) parts.push(city);
    if (country) parts.push(country);
    return parts.join(', ') || (d.place_name || '').trim();
  }

  // design_id -> printed height in mm, from the API's /preview/{id}.
  const cfHeights = {};

  async function cfResolveHeights(slotIds) {
    const base = ((window.CF_CUBE_CUSTOM || {}).previewApiUrl || '').replace(/\/$/, '');
    if (!base) return;
    const ids = [...new Set((slotIds || [])
      .filter(id => String(id).startsWith(CUSTOM_PREFIX))
      .map(id => String(id).slice(CUSTOM_PREFIX.length))
      .filter(id => id && typeof cfHeights[id] !== 'number'))];
    await Promise.all(ids.map(async (id) => {
      try {
        const res = await fetch(`${base}/preview/${encodeURIComponent(id)}`);
        const data = res.ok ? await res.json() : null;
        if (data && typeof data.model_height_mm === 'number') cfHeights[id] = data.model_height_mm;
      } catch (_) { }
    }));
  }

  /*
   * A DESIGN MADE IN ANOTHER TAB (2026-11-02). The city lists send a visitor to
   * the Custom Location configurator in a new tab, so the Set or Collection being
   * built stays as it is; the design they make there lands in this browser's
   * list (bufferKey) while this page is still open on what it read at load. So
   * this page watches the list - the browser's `storage` event for a write from
   * the other tab, `cf:design:buffered` for the design notice writing in this one
   * (snippets/cf-design-notify), and the tab coming back into view - and says
   * `cf:designs-change` on the document when the designs in it changed, with
   * them and the ones that are new. The pages update what they hold and their
   * pickers (cf-city-picker.js puts a new design in the place that asked for it).
   */
  let cfKnownDesigns = null;
  function cfCheckDesigns() {
    const designs = cfReadCustomDesigns();
    const ids = designs.map(v => v.id);
    if (cfKnownDesigns === null) { cfKnownDesigns = ids; return; }
    const added = designs.filter(v => !cfKnownDesigns.includes(v.id));
    const changed = added.length > 0 || ids.length !== cfKnownDesigns.length;
    cfKnownDesigns = ids;
    if (changed) document.dispatchEvent(new CustomEvent('cf:designs-change', { detail: { designs, added } }));
  }
  // What the page starts from, once the catalogue snippet has said where the designs are kept.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', cfCheckDesigns);
  else cfCheckDesigns();
  window.addEventListener('storage', (e) => {
    if (e.key === null || e.key === (window.CF_CUBE_CUSTOM || {}).bufferKey) cfCheckDesigns();
  });
  document.addEventListener('cf:design:buffered', cfCheckDesigns);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') cfCheckDesigns();
  });

  window.CFCities = {
    CUSTOM_PREFIX,
    isCustomId: (id) => String(id).startsWith(CUSTOM_PREFIX),
    readDesigns: cfReadCustomDesigns,
    designLink: cfDesignLink,
    isOutOfStock,
    label: buildCityLabel,
    lineItem: cityLineItem,
    resolveHeights: cfResolveHeights,
    compositeUrl: cfCompositeUrl
  };
})();
